import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeftRight, Building2, Check, Search, User } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import type { Pagina } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type {
  Cliente,
  CuentaDeServicio,
  Traspaso,
  VistaPreviaDeTraspaso,
} from '@/shared/api/tipos';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { Interruptor } from '@/shared/ui/Interruptor';
import { notificar } from '@/shared/ui/avisos';

const MOTIVO_MINIMO = 10;

export function DialogoDeTraspaso({
  abierto,
  cuenta,
  alCerrar,
  alTraspasar,
}: {
  abierto: boolean;
  cuenta: CuentaDeServicio;
  alCerrar: () => void;
  alTraspasar: (acta: Traspaso) => void;
}) {
  const [busqueda, setBusqueda] = useState('');
  const [elegido, setElegido] = useState<Cliente | null>(null);
  const [motivo, setMotivo] = useState('');
  const [liquidada, setLiquidada] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const previa = useQuery({
    queryKey: ['cuentas', cuenta.id, 'traspaso'],
    queryFn: () => api.get<VistaPreviaDeTraspaso>(`/cuentas/${cuenta.id}/traspaso/vista-previa`),
    enabled: abierto,
  });

  const candidatos = useQuery({
    queryKey: ['clientes', 'traspaso', busqueda],
    queryFn: () =>
      api.get<Pagina<Cliente>>('/clientes', {
        query: { texto: busqueda, tamanio: 6, activo: true },
      }),
    enabled: abierto && busqueda.trim().length >= 3,
  });

  const motivoCorto = motivo.trim().length > 0 && motivo.trim().length < MOTIVO_MINIMO;
  const listo = elegido !== null && motivo.trim().length >= MOTIVO_MINIMO;

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo || elegido === null) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const acta = await api.post<Traspaso>(`/cuentas/${cuenta.id}/traspasar`, {
        clienteNuevoId: elegido.id,
        motivo: motivo.trim(),
        deudaLiquidada: liquidada,
      });

      alTraspasar(acta);
      alCerrar();
      notificar.exito(`Cuenta ${cuenta.numeroCuenta} traspasada a ${elegido.nombre}`);
    } catch (causa) {
      const mensaje =
        causa instanceof ErrorDeApi ? causa.message : 'No se pudo traspasar la cuenta';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Cambiar propietario"
      subtitulo={`Cuenta ${cuenta.numeroCuenta}`}
      icono={ArrowLeftRight}
      ancho="ancho"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        {previa.isPending ? (
          <div className="h-24 animate-pulse rounded-xl bg-armazon-50" />
        ) : previa.data ? (
          <div className="rounded-xl border border-armazon-200 bg-armazon-50/60 p-4">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-armazon-400">
              Titular actual
            </p>
            <p className="text-sm font-semibold uppercase text-armazon-900">
              {previa.data.titularNombre}
            </p>
            <p className="importe text-xs text-armazon-500">{previa.data.titularIdentificacion}</p>

            {previa.data.direccionServicio && (
              <p className="mt-2 text-[11px] uppercase text-armazon-500">
                {previa.data.direccionServicio}
              </p>
            )}

            <div className="mt-3 border-t border-armazon-200 pt-3">
              {!previa.data.deudaDisponible ? (

                <p className="flex items-start gap-2 text-[11px] uppercase leading-relaxed text-amber-800">
                  <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-amber-600" aria-hidden />
                  La deuda del predio no se puede consultar todavía: el módulo de facturación aún no
                  existe. El acta lo dejará constar.
                </p>
              ) : previa.data.deudaPendiente > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-[11px] uppercase text-armazon-500">
                    Deuda del predio ·{' '}
                    {previa.data.facturasPendientes === 1
                      ? '1 factura'
                      : `${previa.data.facturasPendientes} facturas`}
                  </span>
                  <span className="importe text-lg font-bold text-amber-700">
                    ${previa.data.deudaPendiente.toFixed(2)}
                  </span>
                </div>
              ) : (
                <p className="text-[11px] uppercase text-emerald-700">El predio está al día</p>
              )}
            </div>
          </div>
        ) : null}

        <Campo
          etiqueta="Nuevo titular"
          requerido
          ayuda="BUSQUE POR NOMBRE, CÉDULA/RUC O CORREO. SOLO SALEN CLIENTES ACTIVOS."
        >
          {elegido ? (
            <div className="flex items-center gap-3 rounded-lg border border-marca-300 bg-marca-50 px-3 py-2.5">
              <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-white text-marca-600">
                {elegido.identificacion.length === 13 ? (
                  <Building2 className="size-4" aria-hidden />
                ) : (
                  <User className="size-4" aria-hidden />
                )}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold uppercase text-armazon-900">
                  {elegido.nombre}
                </span>
                <span className="importe block text-[11px] text-armazon-500">
                  {elegido.identificacion}
                </span>
              </span>
              <button
                type="button"
                onClick={() => {
                  setElegido(null);
                  setBusqueda('');
                }}
                className="shrink-0 text-[11px] font-semibold uppercase text-marca-700 underline underline-offset-2"
              >
                Cambiar
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3.5 top-1/2 size-[15px] -translate-y-1/2 text-armazon-400"
                  aria-hidden
                />
                <input
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className={`${claseEntrada} pl-10`}
                  placeholder="Nombre, cédula o RUC…"
                  autoFocus
                />
              </div>

              {busqueda.trim().length >= 3 && (
                <div className="mt-2 max-h-52 overflow-y-auto rounded-lg border border-armazon-200">
                  {candidatos.isPending ? (
                    <p className="px-3 py-3 text-xs uppercase text-armazon-400">Buscando…</p>
                  ) : (candidatos.data?.contenido.length ?? 0) === 0 ? (
                    <p className="px-3 py-3 text-xs uppercase text-armazon-400">
                      Ningún cliente activo coincide
                    </p>
                  ) : (
                    <ul className="divide-y divide-armazon-100">
                      {(candidatos.data?.contenido ?? [])

                        .filter((c) => c.id !== previa.data?.titularId)
                        .map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => setElegido(c)}
                              className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-armazon-50"
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-sm uppercase text-armazon-800">
                                  {c.nombre}
                                </span>
                                <span className="importe block text-[11px] text-armazon-500">
                                  {c.identificacion}
                                </span>
                              </span>
                              <Check className="size-4 shrink-0 text-armazon-300" aria-hidden />
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </div>
              )}
            </>
          )}
        </Campo>

        <Campo
          etiqueta="Motivo del traspaso"
          requerido
          error={motivoCorto ? `Escriba al menos ${MOTIVO_MINIMO} caracteres` : undefined}
          ayuda="ES LA ÚNICA EXPLICACIÓN QUE QUEDA EN EL ACTA: FALLECIMIENTO, COMPRA DEL BIEN, DONACIÓN…"
        >
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            maxLength={500}
            className={`${claseEntrada} resize-none`}
            placeholder="Cambio de usuario por compra del bien"
          />
        </Campo>

        {previa.data?.deudaDisponible && previa.data.deudaPendiente > 0 && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <span className="mt-0.5 shrink-0">
              <Interruptor
                activo={liquidada}
                alCambiar={() => setLiquidada((v) => !v)}
                etiqueta="La deuda se liquidó en este acto"
              />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-medium uppercase text-amber-900">
                La deuda se liquidó en este acto
              </span>
              <span className="mt-0.5 block text-justify text-[11px] uppercase leading-relaxed text-amber-800">
                Márquelo solo si se cobró aquí mismo. Queda escrito en el acta; si no se marca, la
                deuda sigue colgada del predio y el nuevo titular la verá desde el primer día.
              </span>
            </span>
          </div>
        )}

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar="Confirmar traspaso"
        />
      </form>
    </Modal>
  );
}
